import { InformacoesBancariasCustodiante, InstrumentoFinanceiro } from 'common/genericTypes';
import { typedAction } from 'store/modules/base';

export interface OfertaPrimariaInvestidor {
  investidorId: string;
  quantidade: number;
  precoUnitario: number;
  cpfCnpj?: string;
  contaDeposito?: string;
}

export interface OfertaPrimariaState {
  investidores: Array<OfertaPrimariaInvestidor>;
  informacoesBancarias: Array<InformacoesBancariasCustodiante>;
}

export interface OperacaoState {
  id?: string;
  instrumentoFinanceiroId?: string;
  tipoInstrumentoFinanceiro?: InstrumentoFinanceiro;
  criteriosRemuneracao?: {
    valorNominalUnitarioEmissao?: number;
  };
  dataAgendamento?: Date | null;
  ofertaPrimaria?: OfertaPrimariaState;
}

interface OperacaoField {
  name: keyof Omit<OperacaoState, 'ofertaPrimaria'>;
  value: string | number | undefined | object;
}

interface OfertaPrimariaField {
  name: keyof OfertaPrimariaState;
  value: Array<OfertaPrimariaInvestidor> | Array<InformacoesBancariasCustodiante>;
}

const initialOfertaPrimariaState: OfertaPrimariaState = {
  investidores: [],
  informacoesBancarias: [],
};

const initialOperacaoState: OperacaoState = {
  ofertaPrimaria: initialOfertaPrimariaState,
};

enum OperacaoTypedAction {
  OperacaoUpdate = 'operacao/UPDATE',
  OperacaoUpdateField = 'operacao/UPDATEFIELD',
  OperacaoReset = 'operacao/RESET',
  OfertaPrimariaUpdate = 'operacao/ofertaprimaria/UPDATE',
  OfertaPrimariaUpdateField = 'operacao/ofertaprimaria/UPDATEFIELD',
  OfertaPrimariaRemoveField = 'operacao/ofertaprimaria/REMOVEFIELD',
  OfertaPrimariaReset = 'operacao/ofertaprimaria/RESET',
}

export const updateOperacao = (operacao: OperacaoState) => {
  return typedAction(OperacaoTypedAction.OperacaoUpdate, operacao);
};

export const updateOperacaoField = (field: OperacaoField) => {
  return typedAction(OperacaoTypedAction.OperacaoUpdateField, field);
};

export const resetOperacao = () => {
  return typedAction(OperacaoTypedAction.OperacaoReset);
};

export const updateOfertaPrimaria = (ofertaPrimaria: OfertaPrimariaState) => {
  return typedAction(OperacaoTypedAction.OfertaPrimariaUpdate, ofertaPrimaria);
};

export const updateOfertaPrimariaField = (field: OfertaPrimariaField) => {
  return typedAction(OperacaoTypedAction.OfertaPrimariaUpdateField, field);
};

export const removeOfertaPrimariaField = (field: OfertaPrimariaField) => {
  return typedAction(OperacaoTypedAction.OfertaPrimariaRemoveField, field);
};


export const resetOfertaPrimaria = () => {
  return typedAction(OperacaoTypedAction.OfertaPrimariaReset);
};

type OperacaoRegistroAction = ReturnType<
  | typeof updateOperacao
  | typeof updateOperacaoField
  | typeof resetOperacao
  | typeof updateOfertaPrimaria
  | typeof updateOfertaPrimariaField  
  | typeof removeOfertaPrimariaField
  | typeof resetOfertaPrimaria
>;

export function operacaoReducer(
  state: OperacaoState = initialOperacaoState,
  action: OperacaoRegistroAction
): OperacaoState {
  switch (action.type) {
    case OperacaoTypedAction.OperacaoUpdate:
      return { ...action.payload };
    case OperacaoTypedAction.OperacaoUpdateField:
      return { ...state, [action.payload.name]: action.payload.value };
    case OperacaoTypedAction.OperacaoReset:
      return { ...initialOperacaoState };
    case OperacaoTypedAction.OfertaPrimariaUpdate:
      return { ...state, ofertaPrimaria: action.payload };
    case OperacaoTypedAction.OfertaPrimariaUpdateField:
      return {
        ...state,
        ofertaPrimaria: { ...state.ofertaPrimaria, [action.payload.name]: action.payload.value } as OfertaPrimariaState,
      };
    case OperacaoTypedAction.OfertaPrimariaRemoveField:
      if (!state.ofertaPrimaria) {
        return state; // Retorna o estado original se `ofertaPrimaria` for undefined
      }
    
      if (action.payload.name === 'investidores') {
        const investidoresToRemove = action.payload.value as OfertaPrimariaInvestidor[]; // Cast para o tipo correto
        const idsToRemove = investidoresToRemove.map((investidor) => investidor.investidorId);
    
        return {
          ...state,
          ofertaPrimaria: {
            ...state.ofertaPrimaria,
            investidores: state.ofertaPrimaria.investidores.filter(
              (investidor) => !idsToRemove.includes(investidor.investidorId)
            ),
          },
        };
      } 
      
      return {
        ...state,
        ofertaPrimaria: {
          ...state.ofertaPrimaria,
          [action.payload.name]: action.payload.value,
        } as OfertaPrimariaState,
      };
    case OperacaoTypedAction.OfertaPrimariaReset:
      return { ...state, ofertaPrimaria: initialOfertaPrimariaState };
    default:
      return state;
  }
}
